import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import * as Sentry from '@sentry/angular';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';

// Derive the Sentry environment tag from the hostname at runtime so we don't
// need a per-stage pipeline variable. environment.production is true for any
// deployed build (release.ts); the hostname then disambiguates dev/staging/prod.
function detectSentryEnvironment(): string {
  if (!environment.production) return 'local';
  const host = (typeof window !== 'undefined' && window.location?.hostname) || '';
  if (host.startsWith('dev-') || host.startsWith('dev.')) return 'development';
  if (host.startsWith('staging-') || host.startsWith('staging.')) return 'staging';
  return 'production';
}

// Initialize Sentry BEFORE the config fetch + bootstrap so it captures any
// error during init (including a failed config.json fetch). Empty DSN
// (local dev) is a no-op.
if (environment.sentry?.dsn) {
  Sentry.init({
    dsn: environment.sentry.dsn,
    environment: detectSentryEnvironment(),
    // Release tag = <appVersion>. The pipeline composes appVersion as
    // "<package.json version>-build.<Build.BuildId>" in the BuildVars step, so
    // every ADO build gets a unique semver-valid release ID without needing a
    // separate sha concat. The token-substituted appVersion field already has
    // the BuildId baked in.
    release: `cufi-forms-ui@${environment.appVersion}`,
    tracesSampleRate: environment.sentry.tracesSampleRate,
    integrations: [
      // Browser performance tracing (route changes, fetch/xhr timings).
      Sentry.browserTracingIntegration(),
    ],
    // Forward correlation id from API responses so a Sentry event links to
    // the API trace it occurred during.
    tracePropagationTargets: [
      /^\//,
      /^https:\/\/(dev-api|staging-api|forms-api|api)\.cufi\.org/,
    ],
    // Drop noisy errors that are user environment, not app bugs.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
    ],
    // PII off by default. The form-rendering site collects donor / contact
    // info; we explicitly NEVER want Sentry to autocapture it.
    sendDefaultPii: false,
  });
}

// Fetch runtime config before Angular bootstraps. RecaptchaLoaderService reads
// RECAPTCHA_V3_SITE_KEY once in its constructor and loads the Google script with
// that key. If the key is empty at that point, v3 execute() fails permanently.
fetch('assets/config.json')
  .then(res => res.json())
  .then(config => {
    (window as any).__runtimeConfig = config;
    platformBrowserDynamic().bootstrapModule(AppModule)
      .catch(err => console.error(err));
  })
  .catch(() => {
    platformBrowserDynamic().bootstrapModule(AppModule)
      .catch(err => console.error(err));
  });
